/**
 * Places archive — OpenStreetMap via Leaflet.
 *
 * Interaction model:
 *  - Places view: 80px square thumbnail markers for each place.
 *    • Hover: reveal stage thumbnail squares to the right of the marker.
 *    • Click: drill down into Stages view for that place.
 *  - Stages view: 56px square thumbnail markers for stages of the selected place.
 *    • Click: open popup with stage card.
 *    • Back button: return to Places view.
 *  - Grid cards: clicking a place card in the grid below the map triggers the
 *    same drill-down as clicking the map marker, then scrolls map into view.
 */
import L from 'leaflet';

const FALLBACK_CENTER = [ 46.6034, 1.8883 ];
const FALLBACK_ZOOM   = 6;
// Read later from css var
let PLACE_SIZE      = 80;
let STAGE_SIZE      = 60;
// Height of the downward-pointing arrow rendered below each marker square.
// Included in iconSize so the arrow tip aligns to the exact map coordinate.
let ARROW_HEIGHT    = 12;

export function initPlacesMap() {
	const container = document.getElementById( 'places-map' );
	if ( ! container ) return;

	const places = JSON.parse( container.dataset.places || '[]' );
	const zoomOnStages = (container.dataset.zoomStagesOnClick || 'true') === 'true';
	console.log(zoomOnStages);

	// Read sizes from css variables
	const bodyStyle = window.getComputedStyle(document.body);
	PLACE_SIZE = parseInt(bodyStyle.getPropertyValue('--places-map-place-marker-size').replace('px', '')) || PLACE_SIZE;
	STAGE_SIZE = parseInt(bodyStyle.getPropertyValue('--places-map-stage-marker-size').replace('px', '')) || STAGE_SIZE;
	ARROW_HEIGHT = parseInt(bodyStyle.getPropertyValue('--places-map-marker-arrow-size').replace('px', '')) || ARROW_HEIGHT;

	const map = L.map( container, {
		scrollWheelZoom: false,
		zoomControl: true,
	} );

	L.tileLayer( 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
		attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
		maxZoom: 19,
	} ).addTo( map );

	if ( ! places.length ) {
		map.setView( FALLBACK_CENTER, FALLBACK_ZOOM );
		return;
	}

	// ── Back button control ────────────────────────────────────────────────────

	const BackControl = L.Control.extend( {
		options: { position: 'topleft' },
		onAdd() {
			const btn = L.DomUtil.create( 'button', 'places-map__back' );
			btn.type        = 'button';
			btn.innerHTML 	= '<i class="mf-icon mf-arrow-left"></i> Tous les lieux';
			btn.hidden      = true;
			L.DomEvent.on( btn, 'click', showPlacesView );
			this._btn = btn;
			return btn;
		},
		show() { this._btn.hidden = false; },
		hide() { this._btn.hidden = true; },
	} );

	const backControl = new BackControl();
	backControl.addTo( map );

	// ── Layer groups ───────────────────────────────────────────────────────────

	const placesLayer = L.layerGroup().addTo( map );
	const stagesLayer = L.layerGroup();          // not added until drill-down

	// ── Place markers ──────────────────────────────────────────────────────────

	places.forEach( ( place ) => {
		const { id, lat, lng, title, thumbnail, stages = [] } = place;

		const thumbHtml = thumbnail
			? `<img src="${ thumbnail }" alt="${ escAttr( title ) }" loading="lazy" class="place-marker__img">`
			: `<span class="place-marker__fallback">${ escHtml( title ) }</span>`;

		const stageThumbs = stages.map( ( s ) => {
			const sImg = s.thumbnail
				? `<img src="${ s.thumbnail }" alt="${ escAttr( s.title ) }" loading="lazy">`
				: `<span class="stage-thumb__fallback">${ escHtml( s.title ) }</span>`;
			return `<span class="stage-thumb" title="${ escAttr( s.title ) }">${ sImg }</span>`;
		} ).join( '' );

		const icon = L.divIcon( {
			className:  'place-marker',
			// __inner wraps square + stages so __stages top:50% references the square height,
			// not the taller icon box (which includes the arrow).
			html: `
				<span class="place-marker__inner">
					<span class="place-marker__square">${ thumbHtml }</span>
					${ stageThumbs ? `<span class="place-marker__stages">${ stageThumbs }</span>` : '' }
				</span>
				<span class="place-marker__arrow" aria-hidden="true"></span>
			`,
			iconSize:   [ PLACE_SIZE, PLACE_SIZE + ARROW_HEIGHT ],
			iconAnchor: [ PLACE_SIZE / 2, PLACE_SIZE + ARROW_HEIGHT ], // tip = center bottom
		} );

		const marker = L.marker( [ lat, lng ], { icon } );

		marker.on( 'mouseover', () => marker.getElement()?.classList.add( 'is-hovered' ) );
		marker.on( 'mouseout',  () => marker.getElement()?.classList.remove( 'is-hovered' ) );
		marker.on( 'click',     () => showStagesView( place ) );

		placesLayer.addLayer( marker );
	} );

	// ── Stage marker factory ───────────────────────────────────────────────────

	function buildStageMarkers( place ) {
		stagesLayer.clearLayers();
		const points = [];

		( place.stages || [] ).forEach( ( stage ) => {
			const { id, lat, lng, title, thumbnail } = stage;

			const thumbHtml = thumbnail
				? `<img src="${ thumbnail }" alt="${ escAttr( title ) }" loading="lazy" class="stage-marker__img">`
				: `<span class="stage-marker__fallback">${ escHtml( title ) }</span>`;

			const icon = L.divIcon( {
				className:  'stage-marker',
				html:       `<span class="stage-marker__square">${ thumbHtml }</span><span class="stage-marker__arrow" aria-hidden="true"></span>`,
				iconSize:   [ STAGE_SIZE, STAGE_SIZE + ARROW_HEIGHT ],
				iconAnchor: [ STAGE_SIZE / 2, STAGE_SIZE + ARROW_HEIGHT ], // tip = center bottom
			} );

			const marker = L.marker( [ lat, lng ], { icon } );

			marker.on( 'mouseover', () => marker.getElement()?.classList.add( 'is-hovered' ) );
			marker.on( 'mouseout',  () => marker.getElement()?.classList.remove( 'is-hovered' ) );

			const cardEl   = document.querySelector( `[data-stage-id="${ id }"]` );
			const popupHtml = cardEl ? cardEl.innerHTML : '';

			marker.bindPopup( popupHtml, {
				className: 'stage-popup-pane',
				maxWidth:  320,
			} );

			stagesLayer.addLayer( marker );
			points.push( [ lat, lng ] );
		} );

		return points;
	}

	// ── Initial bounds ─────────────────────────────────────────────────────────

	let allBounds;

	if ( places.length === 1 ) {
		allBounds = null;
		map.setView( [ places[ 0 ].lat, places[ 0 ].lng ], 14 );
	} else {
		allBounds = L.latLngBounds( places.map( ( p ) => [ p.lat, p.lng ] ) );
		map.fitBounds( allBounds, { padding: [ PLACE_SIZE + 24, 24 ] } );
	}

	// ── View transitions ───────────────────────────────────────────────────────

	function showStagesView( place ) {
		if (!zoomOnStages && place.url) {
			window.location = place.url;
			return;
		}

		map.closePopup();
		placesLayer.remove();
		stagesLayer.addTo( map );

		const points = buildStageMarkers( place );
		backControl.show();

		if ( points.length === 0 ) {
			map.setView( [ place.lat, place.lng ], 16 );
		} else if ( points.length === 1 ) {
			map.setView( points[ 0 ], 16 );
		} else {
			map.fitBounds( L.latLngBounds( points ), { padding: [ 60, 60 ], maxZoom: 16 } );
		}

		// Highlight the active card in the grid
		document.querySelectorAll( '[data-place-id]' ).forEach( ( el ) => {
			el.classList.toggle( 'is-active', Number( el.dataset.placeId ) === place.id );
		} );
	}

	function showPlacesView() {
		map.closePopup();
		stagesLayer.remove();
		stagesLayer.clearLayers();
		placesLayer.addTo( map );
		backControl.hide();

		if ( allBounds ) {
			map.fitBounds( allBounds, { padding: [ 48, 48 ] } );
		}

		document.querySelectorAll( '[data-place-id]' ).forEach( ( el ) => {
			el.classList.remove( 'is-active' );
		} );
	}

	// ── Sync grid card clicks ──────────────────────────────────────────────────

	document.querySelectorAll( '[data-place-id]' ).forEach( ( el ) => {
		el.addEventListener( 'click', ( e ) => {
			// Let inner <a> links navigate normally
			if ( e.target.closest( 'a' ) ) return;

			const placeId = Number( el.dataset.placeId );
			const place   = places.find( ( p ) => p.id === placeId );
			if ( ! place ) return;

			showStagesView( place );
			container.scrollIntoView( { behavior: 'smooth', block: 'start' } );
		} );
	} );

	// ── Utilities ──────────────────────────────────────────────────────────────

	function escHtml( str ) {
		return String( str )
			.replace( /&/g, '&amp;' )
			.replace( /</g, '&lt;' )
			.replace( />/g, '&gt;' )
			.replace( /"/g, '&quot;' );
	}

	function escAttr( str ) {
		return escHtml( str ).replace( /'/g, '&#39;' );
	}
}
